import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Wrench,
  Search,
  Filter,
  Loader2,
  X,
  Table,
  AlertCircle,
  MoreVertical
} from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useBodyLock } from '../../hooks/useBodyLock';

interface Mesa {
  id: number;
  numero: string;
  nome: string;
  capacidade: number;
  status: 'livre' | 'ocupada' | 'reservada' | 'manutencao';
  empresa_id: number;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

interface MesaFormData {
  numero: string;
  nome: string;
  capacidade: number;
  status: 'livre' | 'ocupada' | 'reservada' | 'manutencao';
}

const MesasPage: React.FC = () => {
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMesa, setEditingMesa] = useState<Mesa | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('todos');
  const [formData, setFormData] = useState<MesaFormData>({
    numero: '',
    nome: '',
    capacidade: 4,
    status: 'livre'
  });

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);

  // Bloquear scroll do body quando modal aberto no mobile
  useBodyLock(showModal);

  // Carregar mesas
  const loadMesas = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/mesas');
      setMesas(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar mesas:', error);
      toast.error('Erro ao carregar mesas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMesas();
  }, []);

  // Filtrar mesas
  const filteredMesas = mesas.filter(mesa => {
    const matchesSearch = mesa.numero.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         mesa.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'todos' || mesa.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Abrir modal para criar/editar
  const handleOpenModal = (mesa?: Mesa) => {
    if (mesa) {
      setEditingMesa(mesa);
      setFormData({
        numero: mesa.numero,
        nome: mesa.nome,
        capacidade: mesa.capacidade,
        status: mesa.status
      });
    } else {
      setEditingMesa(null);
      setFormData({
        numero: '',
        nome: '',
        capacidade: 4,
        status: 'livre'
      });
    }
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMesa(null);
    setFormData({
      numero: '',
      nome: '',
      capacidade: 4,
      status: 'livre'
    });
  };

  // Salvar mesa
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingMesa) {
        await api.put(`/api/mesas/${editingMesa.id}`, formData);
        toast.success('Mesa atualizada com sucesso!');
      } else {
        await api.post('/api/mesas', formData);
        toast.success('Mesa criada com sucesso!');
      }
      
      await loadMesas();
      handleCloseModal();
    } catch (error: any) {
      console.error('Erro ao salvar mesa:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar mesa');
    } finally {
      setSaving(false);
    }
  };

  // Excluir mesa
  const handleDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta mesa?')) return;
    
    setDeleting(id);
    try {
      await api.delete(`/api/mesas/${id}`);
      toast.success('Mesa excluída com sucesso!');
      await loadMesas();
    } catch (error: any) {
      console.error('Erro ao excluir mesa:', error);
      toast.error(error.response?.data?.error || 'Erro ao excluir mesa');
    } finally {
      setDeleting(null);
    }
  };

  // Alterar status da mesa
  const handleStatusChange = async (id: number, newStatus: string) => {
    try {
      await api.put(`/api/mesas/${id}/status`, { status: newStatus });
      toast.success('Status da mesa atualizado!');
      await loadMesas();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      toast.error(error.response?.data?.error || 'Erro ao alterar status');
    }
  };

  // Estatísticas
  const stats = {
    total: mesas.length,
    livre: mesas.filter(m => m.status === 'livre').length,
    ocupada: mesas.filter(m => m.status === 'ocupada').length,
    reservada: mesas.filter(m => m.status === 'reservada').length,
    manutencao: mesas.filter(m => m.status === 'manutencao').length
  };

  // Obter ícone do status
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'livre':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'ocupada':
        return <Users className="h-5 w-5 text-red-500" />;
      case 'reservada':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'manutencao':
        return <Wrench className="h-5 w-5 text-gray-500" />;
      default:
        return <XCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  // Obter cor do status
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'livre':
        return 'bg-green-100 text-green-800';
      case 'ocupada':
        return 'bg-red-100 text-red-800';
      case 'reservada':
        return 'bg-yellow-100 text-yellow-800';
      case 'manutencao':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Obter texto do status
  const getStatusText = (status: string) => {
    switch (status) {
      case 'livre':
        return 'Livre';
      case 'ocupada':
        return 'Ocupada';
      case 'reservada':
        return 'Reservada';
      case 'manutencao':
        return 'Manutenção';
      default:
        return 'Desconhecido';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48 md:h-64 lg:h-80">
        <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <Table className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-blue-600" />
              Gerenciar Mesas
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Configure e gerencie as mesas do seu estabelecimento
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="flex items-center justify-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors text-sm md:text-base font-medium w-full md:w-auto"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            Nova Mesa
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 lg:gap-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Table className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Livre</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.livre}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 md:h-5 md:w-5 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Ocupada</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.ocupada}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
              <Clock className="h-4 w-4 md:h-5 md:w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Reservada</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.reservada}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <Wrench className="h-4 w-4 md:h-5 md:w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Manutenção</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.manutencao}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 lg:p-5">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <Filter className="h-4 w-4 md:h-5 md:w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
            Filtros
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por número ou nome..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="todos">Todos os status</option>
              <option value="livre">Livre</option>
              <option value="ocupada">Ocupada</option>
              <option value="reservada">Reservada</option>
              <option value="manutencao">Manutenção</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de mesas */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {filteredMesas.length === 0 ? (
          <div className="text-center py-8 md:py-12 px-4">
            <Users className="mx-auto h-10 w-10 md:h-12 md:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm md:text-base font-medium text-gray-900 dark:text-white">
              Nenhuma mesa encontrada
            </h3>
            <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || filterStatus !== 'todos' 
                ? 'Tente ajustar os filtros de busca.'
                : 'Comece criando sua primeira mesa.'
              }
            </p>
          </div>
        ) : (
          <>
            {/* Cards para Mobile */}
            <div className="block md:hidden p-3 md:p-4 space-y-3">
              {filteredMesas.map((mesa) => (
                <div
                  key={mesa.id}
                  className={`border-2 rounded-lg p-3 transition-all ${
                    mesa.status === 'livre' ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' :
                    mesa.status === 'ocupada' ? 'border-red-300 dark:border-red-700 bg-red-50/50 dark:bg-red-900/10' :
                    mesa.status === 'reservada' ? 'border-yellow-300 dark:border-yellow-700 bg-yellow-50/50 dark:bg-yellow-900/10' :
                    'border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-base font-bold text-gray-900 dark:text-white">
                          {mesa.numero}
                        </h4>
                        {getStatusIcon(mesa.status)}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        {mesa.nome}
                      </p>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-600 dark:text-gray-400">
                          <Users className="h-3.5 w-3.5 inline mr-1" />
                          {mesa.capacidade} pessoas
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(mesa.status)}`}>
                          {getStatusText(mesa.status)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleOpenModal(mesa)}
                        className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(mesa.id)}
                        disabled={deleting === mesa.id}
                        className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                        title="Excluir"
                      >
                        {deleting === mesa.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Tabela para Desktop */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Mesa
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Capacidade
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 lg:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredMesas.map((mesa) => (
                    <tr key={mesa.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 md:gap-3">
                          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center ${
                            mesa.status === 'livre' ? 'bg-green-100 dark:bg-green-900/30' :
                            mesa.status === 'ocupada' ? 'bg-red-100 dark:bg-red-900/30' :
                            mesa.status === 'reservada' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                            'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            <Table className={`h-5 w-5 md:h-6 md:w-6 ${
                              mesa.status === 'livre' ? 'text-green-600 dark:text-green-400' :
                              mesa.status === 'ocupada' ? 'text-red-600 dark:text-red-400' :
                              mesa.status === 'reservada' ? 'text-yellow-600 dark:text-yellow-400' :
                              'text-gray-600 dark:text-gray-400'
                            }`} />
                          </div>
                          <div>
                            <div className="text-sm md:text-base font-semibold text-gray-900 dark:text-white">
                              {mesa.numero}
                            </div>
                            <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                              {mesa.nome}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm md:text-base text-gray-900 dark:text-white">
                          <Users className="h-4 w-4 text-gray-400" />
                          <span>{mesa.capacidade} pessoas</span>
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(mesa.status)}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(mesa.status)}`}>
                            {getStatusText(mesa.status)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenModal(mesa)}
                            className="p-2 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4 md:h-5 md:w-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(mesa.id)}
                            disabled={deleting === mesa.id}
                            className="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50"
                            title="Excluir"
                          >
                            {deleting === mesa.id ? (
                              <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 md:h-5 md:w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal de criar/editar mesa */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleCloseModal}></div>
            
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4 md:mb-6">
                    <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-900 dark:text-white">
                      {editingMesa ? 'Editar Mesa' : 'Nova Mesa'}
                    </h3>
                    <button
                      type="button"
                      onClick={handleCloseModal}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  
                  <div className="space-y-3 md:space-y-4">
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Número da Mesa <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.numero}
                        onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                        className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Ex: 01, 02, A1, B2..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Nome da Mesa <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                        placeholder="Ex: Mesa 1, Mesa VIP, Balcão..."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Capacidade <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        required
                        min="1"
                        max="20"
                        value={formData.capacidade}
                        onChange={(e) => setFormData({ ...formData, capacidade: parseInt(e.target.value) || 1 })}
                        className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                        className="block w-full border-2 border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-all"
                      >
                        <option value="livre">Livre</option>
                        <option value="ocupada">Ocupada</option>
                        <option value="reservada">Reservada</option>
                        <option value="manutencao">Manutenção</option>
                      </select>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 flex flex-col sm:flex-row-reverse gap-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-sm md:text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:w-auto"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        {editingMesa ? 'Atualizar' : 'Criar'}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-sm md:text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors sm:w-auto"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MesasPage;
